/**
 * Event-owned dates and times are wall-clock values. They deliberately have no
 * timezone and must never be passed through Date/UTC/offset conversion.
 *
 * These helpers parse only date or naive-local datetime strings. A trailing Z
 * or numeric offset is rejected instead of being stripped or interpreted.
 */

export type WallClockDate = {
  year: number
  month: number
  day: number
}

export type WallClockDateTime = WallClockDate & {
  hour: number
  minute: number
  second: number
  hasSeconds: boolean
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?$/
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

export function parseWallClockDate(value: string | null | undefined): WallClockDate | null {
  const match = DATE_RE.exec(value?.trim() ?? "")
  if (!match) return null
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  return validDate(date) ? date : null
}

export function parseWallClockDateTime(
  value: string | null | undefined,
): WallClockDateTime | null {
  const match = DATE_TIME_RE.exec(value?.trim() ?? "")
  if (!match) return null
  const result = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? 0),
    hasSeconds: match[6] != null,
  }
  if (!validDate(result) || result.hour > 23 || result.minute > 59 || result.second > 59) {
    return null
  }
  return result
}

export function normalizeWallClockDateTime(value: string, withSeconds = true): string {
  const parsed = parseWallClockDateTime(value)
  if (!parsed) {
    throw new Error("Event date/time must be a naive local wall-clock value without Z or an offset")
  }
  return serializeDateTime(parsed, withSeconds)
}

export function wallClockDayKey(value: string | null | undefined): string | null {
  const parsed = parseWallClockDateTime(value) ?? parseWallClockDate(value)
  return parsed ? serializeDate(parsed) : null
}

export function formatWallClockTime(value: string, uppercasePeriod = false): string {
  const parsed = parseWallClockDateTime(value)
  if (!parsed) return ""
  const period = parsed.hour >= 12 ? "pm" : "am"
  const hour = parsed.hour % 12 || 12
  const label = `${hour}:${pad(parsed.minute)}${uppercasePeriod ? " " : ""}${period}`
  return uppercasePeriod ? label.toUpperCase() : label
}

export type WallClockDateStyle =
  | "month-day"
  | "month-day-year"
  | "weekday-short"
  | "weekday-short-year"
  | "weekday-long"

export function formatWallClockDate(
  value: string | null | undefined,
  style: WallClockDateStyle = "month-day",
): string {
  const parsed = parseWallClockDate(value) ?? parseWallClockDateTime(value)
  if (!parsed) return ""
  const month = style === "weekday-long" ? MONTH_LONG[parsed.month - 1] : MONTH_SHORT[parsed.month - 1]
  const core = `${month} ${parsed.day}`
  switch (style) {
    case "month-day": return core
    case "month-day-year": return `${core}, ${parsed.year}`
    case "weekday-short": return `${WEEKDAY_SHORT[weekdayIndex(parsed)]}, ${core}`
    case "weekday-short-year": return `${WEEKDAY_SHORT[weekdayIndex(parsed)]}, ${core}, ${parsed.year}`
    case "weekday-long": return `${WEEKDAY_LONG[weekdayIndex(parsed)]}, ${core}`
  }
}

export function formatWallClockDateTime(
  value: string | null | undefined,
  options: {
    dateStyle?: WallClockDateStyle
    uppercasePeriod?: boolean
    separator?: string
  } = {},
): string {
  const parsed = parseWallClockDateTime(value)
  if (!parsed) return ""
  const normalized = serializeDateTime(parsed, true)
  return `${formatWallClockDate(normalized, options.dateStyle)}${options.separator ?? " · "}${formatWallClockTime(normalized, options.uppercasePeriod)}`
}

export function formatWallClockDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const s = parseWallClockDateTime(start) ?? parseWallClockDate(start)
  if (!s) return ""
  const e = parseWallClockDateTime(end) ?? parseWallClockDate(end)
  if (!e || dayNumber(s) === dayNumber(e)) return formatWallClockDate(serializeDate(s), "month-day-year")
  if (s.year === e.year && s.month === e.month) {
    return `${MONTH_SHORT[s.month - 1]} ${s.day}–${e.day}, ${s.year}`
  }
  if (s.year === e.year) {
    return `${MONTH_SHORT[s.month - 1]} ${s.day} – ${MONTH_SHORT[e.month - 1]} ${e.day}, ${s.year}`
  }
  return `${MONTH_SHORT[s.month - 1]} ${s.day}, ${s.year} – ${MONTH_SHORT[e.month - 1]} ${e.day}, ${e.year}`
}

export function shiftWallClockDate(value: string, deltaDays: number): string {
  const parsed = parseWallClockDate(value)
  if (!parsed) return value
  return serializeDate(dateFromDayNumber(dayNumber(parsed) + Math.trunc(deltaDays)))
}

export function shiftWallClockDateTime(value: string, deltaMinutes: number): string | null {
  const parsed = parseWallClockDateTime(value)
  if (!parsed) return null
  const minutes = dayNumber(parsed) * 1440 + parsed.hour * 60 + parsed.minute + Math.trunc(deltaMinutes)
  const day = floorDiv(minutes, 1440)
  const minuteOfDay = positiveMod(minutes, 1440)
  return serializeDateTime(
    {
      ...dateFromDayNumber(day),
      hour: Math.floor(minuteOfDay / 60),
      minute: minuteOfDay % 60,
      second: parsed.second,
      hasSeconds: parsed.hasSeconds,
    },
    parsed.hasSeconds,
  )
}

export function differenceWallClockMinutes(start: string, end: string): number | null {
  const s = parseWallClockDateTime(start)
  const e = parseWallClockDateTime(end)
  if (!s || !e) return null
  return (dayNumber(e) - dayNumber(s)) * 1440 + (e.hour - s.hour) * 60 + e.minute - s.minute
}

export function differenceWallClockDays(start: string, end: string): number | null {
  const s = parseWallClockDateTime(start) ?? parseWallClockDate(start)
  const e = parseWallClockDateTime(end) ?? parseWallClockDate(end)
  return s && e ? dayNumber(e) - dayNumber(s) : null
}

export function compareWallClockToNow(value: string, now = currentWallClockDateTime()): number | null {
  const parsed = parseWallClockDateTime(value)
  const current = parseWallClockDateTime(now)
  if (!parsed || !current) return null
  const delta = differenceWallClockMinutes(serializeDateTime(current, true), serializeDateTime(parsed, true))
  return delta == null ? null : Math.sign(delta)
}

export function currentWallClockDateTime(now = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

export function currentWallClockDate(now = new Date()): string {
  return currentWallClockDateTime(now).slice(0, 10)
}

export function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) return 0
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

export function weekdayIndex(value: WallClockDate): number {
  const unixEpochDay = dayNumber({ year: 1970, month: 1, day: 1 })
  return positiveMod(dayNumber(value) - unixEpochDay + 4, 7)
}

function validDate(value: WallClockDate): boolean {
  return value.year >= 1 && value.month >= 1 && value.month <= 12 &&
    value.day >= 1 && value.day <= daysInMonth(value.year, value.month)
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

// Howard Hinnant's civil calendar algorithms, using an arbitrary day origin.
// They perform Gregorian calendar math only; there is no instant or timezone.
function dayNumber(value: WallClockDate): number {
  let year = value.year
  year -= value.month <= 2 ? 1 : 0
  const era = floorDiv(year, 400)
  const yearOfEra = year - era * 400
  const shiftedMonth = value.month + (value.month > 2 ? -3 : 9)
  const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + value.day - 1
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear
  return era * 146097 + dayOfEra
}

function dateFromDayNumber(day: number): WallClockDate {
  const era = floorDiv(day, 146097)
  const dayOfEra = day - era * 146097
  const yearOfEra = Math.floor(
    (dayOfEra - Math.floor(dayOfEra / 1460) + Math.floor(dayOfEra / 36524) - Math.floor(dayOfEra / 146096)) / 365,
  )
  let year = yearOfEra + era * 400
  const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100))
  const shiftedMonth = Math.floor((5 * dayOfYear + 2) / 153)
  const date = dayOfYear - Math.floor((153 * shiftedMonth + 2) / 5) + 1
  const month = shiftedMonth + (shiftedMonth < 10 ? 3 : -9)
  year += month <= 2 ? 1 : 0
  return { year, month, day: date }
}

function serializeDate(value: WallClockDate): string {
  return `${value.year.toString().padStart(4, "0")}-${pad(value.month)}-${pad(value.day)}`
}

function serializeDateTime(value: WallClockDateTime, withSeconds: boolean): string {
  const base = `${serializeDate(value)}T${pad(value.hour)}:${pad(value.minute)}`
  return withSeconds ? `${base}:${pad(value.second)}` : base
}

function floorDiv(value: number, divisor: number): number {
  return Math.floor(value / divisor)
}

function positiveMod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor
}

function pad(value: number): string {
  return value.toString().padStart(2, "0")
}
